import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFirebase } from "../context/FirebaseContext";
import { invalidateCachedValue } from "../utility/localDataCache";
import { getUserProfileCacheKey } from "../utility/profileCache";
import { isDemoUserId } from "../utility/demoSession";
import type firebase from "firebase/compat/app";
import {
  applyDocumentLanguage,
  LANGUAGE_SOURCES,
  normalizeLanguage,
  setStoredLanguage
} from "./language";

export const persistLanguageInProfile = async ({ firestore, userId, language }: {
  firestore: firebase.firestore.Firestore;
  userId?: string;
  language: string;
}) => {
  if (!userId || isDemoUserId(userId)) return;

  // Never create a language-only user document. During first registration the
  // auth flow owns creation of the complete profile; this sync only updates a
  // profile that already exists.
  await firestore.collection("users").doc(userId).update({
    language: normalizeLanguage(language)
  });
  await invalidateCachedValue(getUserProfileCacheKey(userId));
};

export const useLanguagePreference = () => {
  const { auth, firestore } = useFirebase();
  const dispatch = useDispatch();
  const language = normalizeLanguage(
    useSelector((state) => state.settings.language)
  );

  const setManualLanguage = useCallback((nextValue: string) => {
    const nextLanguage = normalizeLanguage(nextValue);

    // Persist first: an auth state change can happen immediately after a click,
    // and the bootstrap must already see that this was an explicit choice.
    setStoredLanguage(nextLanguage, LANGUAGE_SOURCES.MANUAL);
    applyDocumentLanguage(nextLanguage);
    dispatch({ type: "setLanguage", payload: nextLanguage });

    const userId = auth.currentUser?.uid;
    if (userId && !isDemoUserId(userId)) {
      void persistLanguageInProfile({ firestore, userId, language: nextLanguage })
        .catch((error: unknown) => {
          // Local UI state is authoritative even if this optional sync fails.
          console.error("Не удалось сохранить язык интерфейса:", error);
        });
    }
  }, [auth, dispatch, firestore]);

  return { language, setManualLanguage };
};
